import { Box, IconButton } from "@mui/material"
import { MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import React, { useRef, useState } from "react"

import TableRowDropdownMenu from "./TableRowDropdownMenu"

import WarningApproveModal from "@/components/common/Modals/WarningApproveModal"
import type { Location } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type {
  LocationConfirmationDialogs,
  LocationMenuDialogs,
} from "@/screens/Companies/edit/components/locations/types/dialogs"
import { useUpdateLocation } from "@/screens/Companies/hooks/useUpdateLocation"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { DialogCloseAction } from "@/types/common"

interface ActionsComponentProps {
  row: CellContext<Location, unknown>["row"]
  setOpenedDialog: (dialog: LocationMenuDialogs | LocationConfirmationDialogs | null) => void
  refetchAfterUpdate: () => void
  isCustomerCompanyType: boolean
}

export const ActionsComponent: React.FC<ActionsComponentProps> = ({
  row,
  setOpenedDialog,
  refetchAfterUpdate,
  isCustomerCompanyType,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)
  const triggerNotification = useNotificationsStore((store) => store.enqueueNotification)

  const { updatingLocation, updateLocation } = useUpdateLocation()
  const handleOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }

  const updateLocationStatus = async (nextActiveStatus: boolean) => {
    const result = await updateLocation(
      {
        locationId: row.original.locationId,
        isActive: nextActiveStatus,
      },
      refetchAfterUpdate
    )
    if (result instanceof Error) {
      triggerNotification({
        message: nextActiveStatus ? "Failed Location Activation" : "Failed Location Deactivation",
        type: NotificationType.Error,
      })
    } else {
      triggerNotification({
        message: nextActiveStatus ? "Location Successfully Activated" : "Location Deactivated",
        type: NotificationType.Success,
      })
    }
  }

  const onToggleActiveStatus = async () => {
    const nextActiveStatus = !row.original.isActive

    if (!nextActiveStatus) {
      setConfirmDialogOpen(true)
      return
    }
    updateLocationStatus(true)
  }

  const handleActiveStatusChange = (reason: DialogCloseAction) => {
    if (reason === "success") {
      updateLocationStatus(false)
    }
    setConfirmDialogOpen(false)
  }

  return (
    <Box className='flex items-center justify-center' ref={anchorElRef}>
      <IconButton onClick={handleOpen}>
        <MoreVertOutlined />
      </IconButton>
      <TableRowDropdownMenu
        onToggleActive={onToggleActiveStatus}
        toggleActiveDisabled={updatingLocation}
        anchorEl={anchorElRef.current}
        open={open}
        isActive={row.original?.isActive}
        onClose={handleClose}
        hasNote={Boolean(row.original?.notes?.length)}
        openDialog={setOpenedDialog}
        hasAddress={Boolean(row.original?.address1)}
        isCustomerCompanyType={isCustomerCompanyType}
      />
      {confirmDialogOpen && (
        <WarningApproveModal
          messages={{
            title: "Are you sure you want to deactivate this location?",
            description:
              "By deactivating this location you will be deactivating all associations to this address, so you won’t be able to continue creating any shipping nor billing actions! Click on “Go Back” if this action was a mistake.",
          }}
          onClose={handleActiveStatusChange}
          submitText='Yes, Deactivate'
        />
      )}
    </Box>
  )
}
